export const STORAGE_KEY = 'readerConfig';

/** Reader configurations that have numeric values. These values can be used
 * with a slider input. */
export interface NumericConfiguration {
  fontSizePercentage: number;
  lineHeight: number;
  lineLength: number;
  letterSpacing: number;
  wordSpacing: number;
  paragraphSpacing: number;
}

export type ColorMode = 'system' | 'light' | 'dark';

export interface ReaderConfiguration extends NumericConfiguration {
  showHighlights: boolean;
  colorMode: ColorMode;
}

export const defaultConfig: ReaderConfiguration = {
  fontSizePercentage: 100,
  lineHeight: 3, // Range from 1 to 5 (140%, 150%, 166%, 180%, 200%), 166% (=3) is default
  lineLength: 2, // Range from 1 to 3 ("Narrow", "Normal", "Wide"), "Normal" (=2) is default
  paragraphSpacing: 2, // Range from 1 to 3 ("Narrow", "Normal", "Wide"), "Normal" (=2) is default
  letterSpacing: 0,
  wordSpacing: 0,
  showHighlights: false,
  colorMode: 'light' as ColorMode, // @TODO: Set to system by default once dark theme is configured properly
};

export interface LineLengthMapValue {
  styleValue: number;
  key: string;
}

export const lineLengthMap = new Map<number, LineLengthMapValue>([
  [1, { styleValue: 50, key: 'narrow' }],
  [2, { styleValue: 60, key: 'normal' }],
  [3, { styleValue: 75, key: 'wide' }],
]);

export const lineHeightMap = new Map<
  number,
  { styleValue: number; key: string }
>([
  [1, { styleValue: 1.4, key: 'narrowest' }],
  [2, { styleValue: 1.5, key: 'narrow' }],
  [3, { styleValue: 1.66, key: 'normal' }],
  [4, { styleValue: 1.8, key: 'wide' }],
  [5, { styleValue: 2, key: 'widest' }],
]);

export const paragraphSpacingMap = new Map<
  number,
  { styleValue: number; key: string }
>([
  [1, { styleValue: 1.2, key: 'narrow' }],
  [2, { styleValue: 1.5, key: 'normal' }],
  [3, { styleValue: 2.0, key: 'wide' }],
]);

const maxLetterSpacingStyleValue = 0.25; // 'em' units
const maxWordSpacingStyleValue = 0.3; // 'em' units

export function sanitizeConfig(config: ReaderConfiguration) {
  return { ...defaultConfig, ...config };
}

export function getStoredConfig() {
  const savedConfig = localStorage.getItem(STORAGE_KEY);

  if (!savedConfig) {
    return defaultConfig;
  }

  return sanitizeConfig({ ...JSON.parse(savedConfig) });
}

/**
 * Calculate the em value by the percentage of the maximum allowed letter spacing value.
 */
export function getLetterSpacing(config: ReaderConfiguration) {
  return config.letterSpacing === 0
    ? 0
    : (maxLetterSpacingStyleValue / 100) * config.letterSpacing;
}
/**
 * Calculate the em value by the percentage of the maximum allowed word spacing value.
 */
export function getWordSpacing(config: ReaderConfiguration) {
  return config.wordSpacing === 0
    ? 0
    : (maxWordSpacingStyleValue / 100) * config.wordSpacing;
}

export function getParagraphSpacingMultiplier(config: ReaderConfiguration) {
  return (
    paragraphSpacingMap.get(config.paragraphSpacing)?.styleValue ??
    paragraphSpacingMap.get(defaultConfig.paragraphSpacing)?.styleValue
  );
}

export function getLineHeight(config: ReaderConfiguration) {
  return (
    lineHeightMap.get(config.lineHeight)?.styleValue ??
    lineHeightMap.get(defaultConfig.lineHeight)?.styleValue
  );
}

export function getFontSizeMultiplier(config: ReaderConfiguration) {
  return config.fontSizePercentage
    ? config.fontSizePercentage / 100
    : defaultConfig.fontSizePercentage / 100;
}

export function getContentWidthCharacterCount(
  config: ReaderConfiguration,
): number {
  const lineLength =
    lineLengthMap.get(config.lineLength)?.styleValue ??
    lineLengthMap.get(defaultConfig.lineLength)?.styleValue;

  return lineLength as number;
}
